import { useEffect, useState } from "react";
import {
  FileTextOutlined,
  ThunderboltOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  TeamOutlined,
  StarOutlined,
  SearchOutlined,
  HeartOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";

const MESSAGES = [
  "Analyzing job requirements and candidate profile…",
  "Finding the beautiful intersection of skills and opportunity…",
  "Matching experience to position requirements…",
  "Uncovering your unique strengths and potential…",
];

const STEPS = ["NURTURING DATA", "UNCOVERING EXCELLENCE", "THOUGHTFUL ANALYSIS"];

interface CardData {
  id: string;
  label: string;
  Icon: React.ComponentType<any>;
  lines: string[];
}

const LEFT_CARDS: CardData[] = [
  { id: "position", label: "POSITION", Icon: FileTextOutlined, lines: ["80%", "55%"] },
  { id: "skills", label: "SKILLS", Icon: ThunderboltOutlined, lines: ["70%", "45%", "60%"] },
  { id: "education", label: "EDUCATION", Icon: ReadOutlined, lines: ["85%", "50%"] },
  { id: "certifications", label: "CERTIFICATIONS", Icon: SafetyCertificateOutlined, lines: ["65%", "40%"] },
  { id: "languages", label: "LANGUAGES", Icon: GlobalOutlined, lines: ["75%", "55%"] },
];

const RIGHT_CARDS: CardData[] = [
  { id: "experience", label: "EXPERIENCE", Icon: TeamOutlined, lines: ["80%", "60%"] },
  { id: "fitscore", label: "FIT SCORE", Icon: StarOutlined, lines: ["90%", "50%"] },
  { id: "keywords", label: "KEYWORDS", Icon: SearchOutlined, lines: ["70%", "45%", "55%"] },
  { id: "culturefit", label: "CULTURE FIT", Icon: HeartOutlined, lines: ["60%", "80%"] },
  { id: "growth", label: "GROWTH", Icon: RiseOutlined, lines: ["75%", "50%"] },
];

const ProcessingScreen = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [activeLeft, setActiveLeft] = useState(0);
  const [activeRight, setActiveRight] = useState(2);

  // Rotate message every 4s
  useEffect(() => {
    const id = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % MESSAGES.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  // Advance step every 3s, stop at last step
  useEffect(() => {
    if (stepIndex >= STEPS.length - 1) return;
    const id = setTimeout(() => setStepIndex((prev) => prev + 1), 3000);
    return () => clearTimeout(id);
  }, [stepIndex]);

  // Cycle left carousel every 2.5s
  useEffect(() => {
    const id = setInterval(() => {
      setActiveLeft((prev) => (prev + 1) % LEFT_CARDS.length);
    }, 2500);
    return () => clearInterval(id);
  }, []);

  // Cycle right carousel every 2.5s (offset: starts at 2)
  useEffect(() => {
    const id = setInterval(() => {
      setActiveRight((prev) => (prev + 1) % RIGHT_CARDS.length);
    }, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="processing-screen">
      {/* Header */}
      <div className="processing-header">
        <Typography.Title level={3}>Understanding Candidate Potential</Typography.Title>
        <Typography.Paragraph className="processing-subtitle" type="secondary">
          AI is thoughtfully synthesizing candidate potential to find the
          perfect alignment...
        </Typography.Paragraph>
      </div>

      {/* Main visual card */}
      <div className="processing-visual-card">
        {/* Left carousel */}
        <div className="processing-carousel left-carousel">
          {LEFT_CARDS.map((card, i) => (
            <div
              key={card.id}
              className={`processing-data-card ${activeLeft === i ? "visible" : "hidden"}`}
            >
              <span className="data-card-label">
                <card.Icon />
                &nbsp;{card.label}
              </span>
              <div className="data-card-lines">
                {card.lines.map((w, j) => (
                  <div key={j} className="data-card-line" style={{ width: w }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Center orb */}
        <div className="processing-center">
          <div className="connecting-line left-line" />
          <div className="processing-orb">
            <img src="/icons/ai-stars.svg" alt="AI" />
          </div>
          <div className="connecting-line right-line" />
        </div>

        {/* Right carousel */}
        <div className="processing-carousel right-carousel">
          {RIGHT_CARDS.map((card, i) => (
            <div
              key={card.id}
              className={`processing-data-card ${activeRight === i ? "visible" : "hidden"}`}
            >
              <span className="data-card-label">
                <card.Icon />
                &nbsp;{card.label}
              </span>
              <div className="data-card-lines">
                {card.lines.map((w, j) => (
                  <div key={j} className="data-card-line" style={{ width: w }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Synthesis badge */}
      <Typography.Paragraph type="secondary" className="synthesis-badge">SYNTHESIS IN PROGRESS</Typography.Paragraph>

      {/* Progress bar + rotating message */}
      <div className="processing-progress-section">
        <Typography.Paragraph className="processing-message">{MESSAGES[messageIndex]}</Typography.Paragraph>
        <div className="processing-progress-bar">
          <div className="processing-progress-fill" />
        </div>
      </div>

      {/* Step indicators */}
      <div className="processing-steps">
        {STEPS.map((label, i) => {
          const state =
            i < stepIndex ? "done" : i === stepIndex ? "active" : "pending";
          return (
            <div key={label} className={`step-indicator ${state}`}>
              <div className="step-dot" />
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProcessingScreen;
